
import { useEffect, useRef, useState } from "react";
import DOMPurify from 'dompurify';
import { Upload, Button, Form, message, Modal, Spin } from "antd";
import {
  UploadOutlined,
  EyeOutlined,
  DeleteOutlined,
  DownloadOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import {
  uploadFile,
  downloadFile,
  deleteFile,
} from "../../../services/WebServices";
import {
  uploadPublicFormAttachment,
  downloadPublicFormAttachment,
  deletePublicFormAttachment,
} from "../../../services/publicFormServices";
import { useParams } from "react-router-dom";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import { useTranslation } from 'react-i18next';

const attachmentsFormats = ['images', 'pdf', 'doc', 'xls', 'pptx', 'csv'];

const formatExtensions = {
  'images': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg'],
  'pdf': ['.pdf'],
  'doc': ['.doc', '.docx'],
  'xls': ['.xls', '.xlsx'],
  'pptx': ['.ppt', '.pptx'],
  'csv': ['.csv']
};

// Word Document Preview Component
const WordPreview = ({ base64Content }) => {
  const { t } = useTranslation();
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadWordDocument = async () => {
      try {
        setLoading(true);
        const arrayBuffer = Uint8Array.from(atob(base64Content), c => c.charCodeAt(0)).buffer;
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setHtmlContent(result.value);
        setError(null);
      } catch (err) {
        setError("Failed to load Word document");
      } finally {
        setLoading(false);
      }
    };

    loadWordDocument();
  }, [base64Content]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div>{t('Loading Word document...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ color: "red" }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{
      width: "100%",
      height: "70vh",
      overflow: "auto",
      padding: "16px",
      backgroundColor: "#fff",
      border: "1px solid #d9d9d9",
      borderRadius: "6px"
    }}>
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }} />
    </div>
  );
};

// Excel File Preview Component
const ExcelPreview = ({ base64Content }) => {
  const { t } = useTranslation();
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [worksheets, setWorksheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);

  useEffect(() => {
    const loadExcelDocument = async () => {
      try {
        setLoading(true);
        const arrayBuffer = Uint8Array.from(atob(base64Content), c => c.charCodeAt(0)).buffer;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        const sheetNames = workbook.SheetNames;
        setWorksheets(sheetNames);

        // Convert first sheet to HTML
        const worksheet = workbook.Sheets[sheetNames[0]];
        const htmlTable = XLSX.utils.sheet_to_html(worksheet);
        setHtmlContent(htmlTable);
        setError(null);
      } catch (err) {
        setError("Failed to load Excel document");
      } finally {
        setLoading(false);
      }
    };

    loadExcelDocument();
  }, [base64Content]);

  const handleSheetChange = async (sheetIndex) => {
    try {
      const arrayBuffer = Uint8Array.from(atob(base64Content), c => c.charCodeAt(0)).buffer;
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[worksheets[sheetIndex]];
      const htmlTable = XLSX.utils.sheet_to_html(worksheet);
      setHtmlContent(htmlTable);
      setActiveSheet(sheetIndex);
    } catch (err) {
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div>{t('Loading Excel document...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ color: "red" }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "70vh" }}>
      {worksheets.length > 1 && (
        <div style={{
          padding: "8px",
          borderBottom: "1px solid #d9d9d9",
          backgroundColor: "#f5f5f5"
        }}>
          {worksheets.map((sheetName, index) => (
            <Button
              key={index}
              size="small"
              type={activeSheet === index ? "primary" : "default"}
              onClick={() => handleSheetChange(index)}
              style={{ marginRight: "8px" }}
            >
              {sheetName}
            </Button>
          ))}
        </div>
      )}
      <div style={{
        width: "100%",
        height: worksheets.length > 1 ? "calc(70vh - 50px)" : "70vh",
        overflow: "auto",
        padding: "16px",
        backgroundColor: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: "6px"
      }}>
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }} />
      </div>
    </div>
  );
};

export const Attachments = (props) => {
  const { t } = useTranslation();
  const { label, tooltip, helpText, mandatory, readOnly, previewMode, multipleAttachments, id, formValues, attachmentFormat = [], shareToken, tenantId, currentStatus, passFormValues } =
    props;
  const disabled = previewMode && readOnly ? true : false
  const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> }
    : undefined;

  // Check if we're in public form mode
  const isPublicForm = !!shareToken;
  // Use attachmentFormat as the source of allowed formats
  const params = useParams();

  const rules = mandatory ? [{ required: true }] : undefined;
  const [docFileList, setDocFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  // Files that were in the list before a single-attachment replace; deleted from the bucket after the new upload succeeds.
  const replacedFilesRef = useRef([]);
  // Concurrent uploads: a later onChange snapshot can still include a file that already failed.
  const failedUploadUidsRef = useRef(new Set());
  const uploadingCountRef = useRef(0);
  const autoSaveTimerRef = useRef(null);
  const maxCount = multipleAttachments ? 15 : 1;
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const normalizeRequestId = (value) => {
    if (value === undefined || value === null) return null;
    const str = String(value).trim();
    if (!str) return null;
    if (str === "new" || str === "undefined" || str === "null") return null;
    return str;
  };

  const requestId = normalizeRequestId(params.requestId || props.requestId);

  // Only images and PDFs get the eye icon; other types are download-only.
  const canPreview = (file) => {
    const ext = "." + (file?.name || "").split(".").pop().toLowerCase();
    return formatExtensions.images.includes(ext) || formatExtensions.pdf.includes(ext);
  };

  const deleteRemote = async (fileName) => {
    if (isPublicForm) {
      const res = await deletePublicFormAttachment(requestId, fileName, shareToken, tenantId);
      return { success: res.status === 200, message: res.message };
    }
    return deleteFile(requestId, fileName);
  };

  // Chrome renders a blank frame for data: PDFs inside an iframe; a blob URL works everywhere.
  const base64ToBlobUrl = (base64, contentType) => {
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    return URL.createObjectURL(new Blob([bytes], { type: contentType }));
  };

  // Persist the request after an upload/delete: draft & requester views expose #draftButton, approval stages #saveButton.
  // Delayed so the form value change propagates through onValuesChange before the save reads currentValues.
  const triggerAutoSave = () => {
    const saveElement = document.getElementById("draftButton") || document.getElementById("saveButton");
    if (!saveElement) return;
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      saveElement.click();
    }, 250);
  };

  useEffect(() => () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
  }, []);

  const isFailedUpload = (file) => {
    if (!file) return true;
    if (file.status === "error") return true;
    return failedUploadUidsRef.current.has(file.uid);
  };

  const getValidFileList = (fileList = []) => fileList.filter((file) => !isFailedUpload(file));

  const persistFileList = (validFileList) => {
    const nextValue = validFileList.length === 0 ? null : { fileList: validFileList };
    if (props.form) {
      props.form.setFieldValue(id, nextValue);
    }
    if (passFormValues) {
      const allValues = props.form?.getFieldsValue(true) || {};
      passFormValues({ ...allValues, [id]: nextValue });
    }
  };

  const readFileAsDataUrl = (fileObj) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(fileObj);
    });

  // Build accept attribute based on configured formats
  const getAcceptAttribute = () => {
    let selectedFormats = attachmentFormat || attachmentsFormats;
    if (selectedFormats && selectedFormats.length > 0) {

      const allowedExtensions = [];
      selectedFormats.forEach(format => {
        if (formatExtensions[format]) {
          allowedExtensions.push(...formatExtensions[format]);
        }
      });

      return allowedExtensions.join(',');
    }

    // Default: allow all safe file types
    return ".jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.rtf,.odt,.ods,.odp,.csv,.txt,.xml,.json,.html,.htm,.md,.markdown";
  };

  useEffect(() => {
    const incoming = formValues?.[id]?.fileList
    if (incoming) {
      setDocFileList(prev => {
        const validIncoming = getValidFileList(incoming)
        if (uploadingCountRef.current > 0) {
          const incomingUids = new Set(validIncoming.map(file => file.uid))
          const localOnly = prev.filter(file => !incomingUids.has(file.uid) && !isFailedUpload(file))
          return [...validIncoming, ...localOnly]
        }
        return validIncoming
      })
    } else {
      if (uploadingCountRef.current > 0) return
      setDocFileList([])
    }
  }, [formValues, id])

  const handleUploadValidation = (file) => {
    if (!requestId) {
      message.error(t('Your request must be saved before you can upload files.'));
      return Upload.LIST_IGNORE;
    }

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split(".").pop();

    // Define dangerous/malicious file extensions to block (always checked first)
    const blockedExtensions = [
      // Executable files
      "exe", "bat", "cmd", "com", "pif", "scr", "vbs", "vbe", "js", "jse", "wsf", "wsh",
      // System files
      "dll", "sys", "drv", "ocx",
      // Script files
      "ps1", "ps2", "psc1", "psc2", "msh", "msh1", "msh2", "mshxml", "msh1xml", "msh2xml",
      // Other potentially dangerous files
      "reg", "inf", "cpl", "msi", "msp", "hta", "jar",
      "html", "htm", "xhtml", // Optionally block HTML files
      "json", // Optionally block JSON files

    ];

    // First check if file extension is blocked (security check)
    if (blockedExtensions.includes(fileExtension)) {
      message.error(`File type .${fileExtension} is not allowed.`);
      return Upload.LIST_IGNORE;
    }

    // Check if specific formats are configured (user restriction)
    let selectedFormats = attachmentFormat || attachmentsFormats;
    if (selectedFormats && selectedFormats.length > 0) {
      // Map format values from AttachmentForm to actual file extensions
      const formatMap = {
        'images': ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg'],
        'pdf': ['pdf'],
        'doc': ['doc', 'docx'],
        'xls': ['xls', 'xlsx'],
        'pptx': ['ppt', 'pptx'],
        'csv': ['csv']
      };

      // Build list of allowed extensions based on configured formats
      const configuredExtensions = [];
      const formatLabels = [];
      selectedFormats.forEach(format => {
        if (formatMap[format]) {
          configuredExtensions.push(...formatMap[format]);
          // Build user-friendly label
          if (format === 'images') {
            formatLabels.push('Images');
          } else if (format === 'pdf') {
            formatLabels.push('PDF');
          } else if (format === 'doc') {
            formatLabels.push('Word Documents');
          } else if (format === 'xls') {
            formatLabels.push('Excel Spreadsheets');
          } else if (format === 'pptx') {
            formatLabels.push('PowerPoint');
          } else if (format === 'csv') {
            formatLabels.push('CSV Files');
          }
        }
      });

      // Check if uploaded file matches configured formats
      if (!configuredExtensions.includes(fileExtension)) {
        const formatNames = formatLabels.join(', ');
        message.error(`Only ${formatNames} are allowed for this field.`);
        return Upload.LIST_IGNORE;
      }
    } else {
      // No specific formats configured - use default allowed types
      const allowedTypes = [
        // Images
        "image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp", "image/tiff", "image/webp", "image/svg+xml",
        // Documents
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/rtf",
        "application/vnd.oasis.opendocument.text",
        "application/vnd.oasis.opendocument.spreadsheet",
        "application/vnd.oasis.opendocument.presentation",
        // Text files
        "text/csv", "text/plain", "text/xml", "application/json", "text/html", "text/markdown"
      ];

      const allowedExtensions = [
        // Images
        "jpg", "jpeg", "png", "gif", "bmp", "tiff", "tif", "webp", "svg",
        // Documents
        "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "rtf", "odt", "ods", "odp",
        // Text files
        "csv", "txt", "xml", "json", "html", "htm", "md", "markdown"
      ];

      // Check if file type is allowed
      const isValidType =
        allowedTypes.includes(file.type) ||
        allowedExtensions.includes(fileExtension);

      if (!isValidType) {
        message.error(t('File type not supported. Allowed types: Images (JPEG, JPG, PNG, GIF, BMP, TIFF, WEBP, SVG), Documents (PDF, DOC/DOCX, XLS/XLSX, PPT/PPTX, RTF, ODT/ODS/ODP), Text files (CSV, TXT, XML, JSON, HTML, MD).'));
        return Upload.LIST_IGNORE;
      }
    }

    // Check for duplicate file names
    let isUnique = true;
    for (let i = 0; i < docFileList.length; i++) {
      let doc = docFileList[i];
      if (doc && doc?.name === file.name) {
        isUnique = false;
        break;
      }
    }

    if (!isUnique) {
      message.error(t('Multiple files with same name cannot be uploaded.'));
      return Upload.LIST_IGNORE;
    }

    // Single attachment: antd replaces the list entry, so remember what was there to purge it from the bucket after upload.
    replacedFilesRef.current = maxCount === 1 ? docFileList.filter(doc => doc?.name && doc.name !== file.name) : [];

    return true;
  };

  const customRequest = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;

    if (!requestId) {
      const err = new Error("Request ID is required for file upload");
      message.error(t('Your request must be saved before you can upload files.'));
      onError(err);
      return;
    }
    uploadingCountRef.current += 1;
    setUploading(true);

    try {
      onProgress({ percent: 10 });

      let response;
      if (isPublicForm) {
        // Use public form upload service
        response = await uploadPublicFormAttachment(requestId, shareToken, file, tenantId);
        // Normalize response format
        response = { success: response.status === 200, data: response.data, message: response.message };
      } else {
        response = await uploadFile(file, requestId);
      }

      onProgress({ percent: 100 });
      if (response.success) {
        failedUploadUidsRef.current.delete(file.uid);
        onSuccess(response.data, file);
        message.success(t('File uploaded successfully'));

        const replaced = replacedFilesRef.current;
        replacedFilesRef.current = [];
        await Promise.all(replaced.map(old => deleteRemote(old.name).catch(() => null)));

        triggerAutoSave();
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      message.error(error.message || "Failed to upload file");

      failedUploadUidsRef.current.add(file.uid);
      setDocFileList((prevList) => getValidFileList(prevList.filter((item) => item.uid !== file.uid)));
      onError(error);
    } finally {
      uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
      setUploading(uploadingCountRef.current > 0);
    }
  };
  const handlePreview = async (file) => {
    // If the file is local (not yet uploaded), preview it without any API call.
    // if (file?.originFileObj) {
    try {
      if (!file.preview && file.url) {
        file.preview = await readFileAsDataUrl(file.originFileObj);
        file.previewType = file.originFileObj.type;
      }

      setPreviewImage(file.preview);
      setPreviewType(file.previewType);
      setPreviewVisible(true);
      setPreviewTitle(file.name);
    } catch (error) {
      message.error(`Failed to load file preview: ${error.message}`);
      return
    }
    // }

    // For already-uploaded files, require a valid requestId before downloading preview content.
    if (!requestId) {
      message.error(t('Request ID is required for file preview'));
      return;
    }

    if (!file.url && !file.preview) {
      try {
        // Download file for preview
        setIsPreviewLoading(true)
        let response;
        if (isPublicForm) {
          response = await downloadPublicFormAttachment(requestId, file.name, shareToken, tenantId);
        } else {
          response = await downloadFile(requestId, file.name);
        }
        setIsPreviewLoading(false)

        // Check detailed path for content
        let content;
        if (response.result?.body?.content) {
          content = response.result.body.content;
        }

        // Check if the response contains data and content was found
        if (response && response.success && content) {
          const headers = response.result.headers || {};

          // Validate base64 content
          if (!content) {
            throw new Error("No file content received from server");
          }

          // Get content type from headers or infer from file extension
          let contentType = "application/octet-stream";
          if (
            headers["Content-Type"] &&
            Array.isArray(headers["Content-Type"])
          ) {
            contentType = headers["Content-Type"][0];
          } else if (typeof headers["Content-Type"] === "string") {
            contentType = headers["Content-Type"];
          } else {
            // Infer content type from file extension
            const extension = file.name.split(".").pop().toLowerCase();
            const mimeTypes = {
              // Images
              jpg: "image/jpeg",
              jpeg: "image/jpeg",
              png: "image/png",
              gif: "image/gif",
              bmp: "image/bmp",
              tiff: "image/tiff",
              tif: "image/tiff",
              webp: "image/webp",
              svg: "image/svg+xml",
              // Documents
              pdf: "application/pdf",
              doc: "application/msword",
              docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              xls: "application/vnd.ms-excel",
              xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ppt: "application/vnd.ms-powerpoint",
              pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
              rtf: "application/rtf",
              odt: "application/vnd.oasis.opendocument.text",
              ods: "application/vnd.oasis.opendocument.spreadsheet",
              odp: "application/vnd.oasis.opendocument.presentation",
              // Text files
              csv: "text/csv",
              txt: "text/plain",
              xml: "text/xml",
              json: "application/json",
              html: "text/html",
              htm: "text/html",
              md: "text/markdown",
              markdown: "text/markdown"
            };
            contentType = mimeTypes[extension] || "application/octet-stream";
          }

          // Store content info for preview
          file.preview = contentType === "application/pdf"
            ? base64ToBlobUrl(content, contentType)
            : `data:${contentType};base64,${content}`;
          file.previewType = contentType;
          file.previewContent = content;
        } else {
          // If no content is found but the response has a success message
          if (response && response.message === "Document download successfully") {
            // Try an alternative approach to find the content
            if (response.result && typeof response.result.body === 'object') {
              // Log all available keys in the response structure to help debug

              // Check alternative paths for content
              const alternativeContent =
                response.result.body.content ||
                (response.result.body.result && response.result.body.result.content);

              if (alternativeContent) {
                // Infer content type from file extension for the alternative path
                const extension = file.name.split(".").pop().toLowerCase();
                const mimeTypes = {
                  // Images
                  jpg: "image/jpeg",
                  jpeg: "image/jpeg",
                  png: "image/png",
                  gif: "image/gif",
                  bmp: "image/bmp",
                  tiff: "image/tiff",
                  tif: "image/tiff",
                  webp: "image/webp",
                  svg: "image/svg+xml",
                  // Documents and other types...
                };
                const inferredContentType = mimeTypes[extension] || "application/octet-stream";

                // We found content in an alternative location, use it
                file.preview = inferredContentType === "application/pdf"
                  ? base64ToBlobUrl(alternativeContent, inferredContentType)
                  : `data:${inferredContentType};base64,${alternativeContent}`;
                file.previewType = inferredContentType;
                file.previewContent = alternativeContent;
              } else {
                throw new Error("The file content is missing or in an unexpected format");
              }
            } else {
              throw new Error("The file content structure is unexpected");
            }
          } else {
            throw new Error(
              response?.message || "Failed to download file for preview"
            );
          }
        }
      } catch (error) {
        message.error(`Failed to load file preview: ${error.message}`);
        return;
      }
    }

    setPreviewImage(file.url || file.preview);
    setPreviewType(file.previewType || "");
    setPreviewVisible(true);
    setPreviewTitle(
      file.name ||
      (file.url ? file.url.substring(file.url.lastIndexOf("/") + 1) : "")
    );
  };

  const handleDownload = async (file) => {
    if (!requestId) {
      message.error(t('Request ID is required for file download'));
      return;
    }

    try {
      setIsDownloading(true);
      let response
      if (isPublicForm) {
        // Use public form download service
        response = await downloadPublicFormAttachment(requestId, file.name, shareToken, tenantId);

      } else {
        response = await downloadFile(requestId, file.name);
      }

      setIsDownloading(false);
      if (response.success && response.result && response.result.body) {
        // The actual content is nested in response.result.body.content
        const content =
          response?.result?.body?.content ||
          response.result.body.content;

        if (!content) {
          throw new Error("No file content received from server");
        }

        // Convert base64 to blob for download
        const byteCharacters = atob(content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        // Determine content type from file extension
        const extension = file.name.split(".").pop().toLowerCase();
        const mimeTypes = {
          // Images
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
          gif: "image/gif",
          bmp: "image/bmp",
          tiff: "image/tiff",
          tif: "image/tiff",
          webp: "image/webp",
          svg: "image/svg+xml",
          // Documents
          pdf: "application/pdf",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          xls: "application/vnd.ms-excel",
          xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          ppt: "application/vnd.ms-powerpoint",
          pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          rtf: "application/rtf",
          odt: "application/vnd.oasis.opendocument.text",
          ods: "application/vnd.oasis.opendocument.spreadsheet",
          odp: "application/vnd.oasis.opendocument.presentation",
          // Text files
          csv: "text/csv",
          txt: "text/plain",
          xml: "text/xml",
          json: "application/json",
          html: "text/html",
          htm: "text/html",
          md: "text/markdown",
          markdown: "text/markdown"
        };
        const contentType = mimeTypes[extension] || "application/octet-stream";

        const blob = new Blob([byteArray], { type: contentType });
        const url = URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL
        setTimeout(() => URL.revokeObjectURL(url), 100);
        message.success(t('File downloaded successfully'));
      } else {
        throw new Error(response.message || "Failed to download file");
      }
    } catch (error) {
      message.error(`Failed to download file: ${error.message}`);
    }
  };
  const handleDelete = async (file) => {
    if (!requestId) {
      message.error(t('Request ID is required for file deletion'));
      return;
    }

    try {
      setIsDeleting(true);

      const response = await deleteRemote(file.name);

      setIsDeleting(false);
      if (response.success) {
        message.success(t('File deleted successfully'));
        const newFileList = docFileList.filter((item) => item.uid !== file.uid);
        setDocFileList(newFileList);
        persistFileList(newFileList);
        triggerAutoSave();
      } else {
        throw new Error(response.message || "Failed to delete file");
      }
    } catch (error) {
      message.error(`Failed to delete file: ${error?.message}`);
    }
  };

  const handleChange = (info) => {
    if (!requestId) {
      return null
    }
    const { fileList } = info;

    // Drop failed files even when a concurrent success onChange still has them in its snapshot.
    const validFileList = getValidFileList(fileList);

    setDocFileList(validFileList);
    persistFileList(validFileList);
  };
  const handleRemove = async (file) => {
    // Use custom delete instead of default remove
    await handleDelete(file);
    return false; // Prevent default remove behavior
  };
  const renderPreviewContent = () => {
    if (!previewImage) return null;

    // Blob URLs (PDF) carry no type in the string, so prefer the stored type; fall back to parsing the data URL.
    const contentType = previewType || previewImage.split(';')[0].split(':')[1];

    // Handle different content types
    if (contentType?.startsWith('image/')) {
      return (
        <img
          alt="preview"
          style={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }}
          src={previewImage}
        />
      );
    } else if (contentType === 'application/pdf') {
      return (
        <div style={{ width: "100%", height: "70vh" }}>
          <iframe
            src={previewImage}
            style={{ width: "100%", height: "100%", border: "none" }}
            title={t('PDF Preview')}
          />
        </div>
      );
    } else if (contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      // Handle Word documents
      return <WordPreview base64Content={previewImage.split(',')[1]} />;
    } else if (contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      // Handle Excel files
      return <ExcelPreview base64Content={previewImage.split(',')[1]} />;
    } else if (contentType?.startsWith('text/')) {
      // Decode base64 content for text files
      const base64Content = previewImage.split(',')[1];
      const textContent = atob(base64Content);
      return (
        <div style={{
          width: "100%",
          height: "70vh",
          overflow: "auto",
          padding: "16px",
          backgroundColor: "#f5f5f5",
          border: "1px solid #d9d9d9",
          borderRadius: "6px"
        }}>
          <pre style={{
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            fontFamily: "monospace",
            fontSize: "14px",
            margin: 0
          }}>
            {textContent}
          </pre>
        </div>
      );
    } else if (contentType === 'application/json') {
      // Decode and format JSON
      const base64Content = previewImage.split(',')[1];
      const jsonContent = atob(base64Content);
      try {
        const formattedJson = JSON.stringify(JSON.parse(jsonContent), null, 2);
        return (
          <div style={{
            width: "100%",
            height: "70vh",
            overflow: "auto",
            padding: "16px",
            backgroundColor: "#f5f5f5",
            border: "1px solid #d9d9d9",
            borderRadius: "6px"
          }}>
            <pre style={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              fontFamily: "monospace",
              fontSize: "14px",
              margin: 0
            }}>
              {formattedJson}
            </pre>
          </div>
        );
      } catch (e) {
        return (
          <div style={{
            width: "100%",
            height: "70vh",
            overflow: "auto",
            padding: "16px",
            backgroundColor: "#f5f5f5",
            border: "1px solid #d9d9d9",
            borderRadius: "6px"
          }}>
            <pre style={{
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              fontFamily: "monospace",
              fontSize: "14px",
              margin: 0
            }}>
              {jsonContent}
            </pre>
          </div>
        );
      }
    } else {
      // For unsupported types, show a message and download option
      return (
        <div style={{
          textAlign: "center",
          padding: "40px",
          backgroundColor: "#f5f5f5",
          borderRadius: "6px"
        }}>
          <div style={{ fontSize: "16px", marginBottom: "16px" }}>
            {t('Preview not available for this file type')}
          </div>
          <div style={{ fontSize: "14px", color: "#666", marginBottom: "24px" }}>
            {t('File type:')} {contentType}
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              // Trigger download
              const link = document.createElement('a');
              link.href = previewImage;
              link.download = previewTitle;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            {t('Download File')}
          </Button>
        </div>
      );
    }
  };

  return (
    <div className="form-element">
      <Form.Item
        name={id}
        label={label}
        tooltip={help}
        rules={rules}
        style={{ textAlign: "left" }}
      >
        {" "}
        <Upload

          disabled={disabled}
          multiple={multipleAttachments}
          maxCount={maxCount}
          accept={getAcceptAttribute()}
          fileList={docFileList}
          customRequest={customRequest}
          beforeUpload={handleUploadValidation}
          onChange={handleChange}
          onRemove={handleRemove}
          onPreview={handlePreview}
          listType="text"
          itemRender={(originNode, file, fileList) => {
            return (
              <div className="file-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "4px 8px",
                  // border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  marginBottom: "4px"
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "#0f95f5"
                  }}
                >
                  {file.name}
                </span>
                <div style={{ display: "flex", gap: "8px", marginLeft: "8px" }}>
                  {
                    !canPreview(file) ? null
                      : isPreviewLoading ? <Spin indicator={<LoadingOutlined spin />} size="small" />
                      : <Button className="file-action-icon"
                        type="text"
                        size="small"
                        disabled={isDownloading || isDeleting}
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(file)}
                        title={t('Preview')}
                      />
                  }
                  {
                    isDownloading ?
                      <Spin indicator={<LoadingOutlined spin />} size="small" />
                      : <Button className="file-action-icon"
                        type="text"
                        size="small"
                        disabled={isPreviewLoading || isDeleting}
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(file)}
                        title={t('Download')}
                      />
                  }

                  {
                    !disabled &&
                    <>
                      {isDeleting ?
                        <Spin indicator={<LoadingOutlined spin />} size="small" />
                        :
                        <Button className="file-action-icon"
                          type="text"
                          size="small"
                          disabled={isPreviewLoading || isDownloading}
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(file)}
                          title={t('Delete')}
                          danger
                        />
                      }
                    </>

                  }
                </div>
              </div>
            );
          }}
        >
          <Button type="primary" style={{ marginBottom: "4px" }} icon={<UploadOutlined />} loading={uploading}>
            {uploading ? t("Uploading...") : t("Upload")}
          </Button>
        </Upload>

      </Form.Item>

      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ top: 20, minHeight: "60vh" }}
      >
        {
          isPreviewLoading ?
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
              <Spin indicator={<LoadingOutlined spin />} size="large" />
            </div>
            : renderPreviewContent()
        }
      </Modal>
    </div>
  );
};
